import { z } from 'zod';
import { DEFAULT_PAGE_SIZE, LIMITS, MAX_PAGE_SIZE } from '../constants';
/**
 * Primitives reused across every schema. Defining them once means a slug is
 * validated identically whether it arrives on a post, a page or a category.
 */
export const uuidSchema = z.uuid({ error: 'Must be a valid UUID.' });
/**
 * Lowercase, hyphen-separated, no leading/trailing/double hyphens.
 * Enforced rather than silently normalised so a user editing a slug by hand
 * gets told what is wrong instead of having their input quietly rewritten.
 */
export const slugSchema = z
    .string()
    .min(LIMITS.slug.min)
    .max(LIMITS.slug.max)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Use lowercase letters, numbers and single hyphens only (e.g. "rpo-vs-in-house").');
export const emailSchema = z
    .email({ error: 'Enter a valid email address.' })
    .trim()
    .toLowerCase()
    .max(254);
/**
 * Password policy: length over composition rules.
 *
 * NIST SP 800-63B advises against mandatory character-class mixing — it pushes
 * people toward predictable substitutions like "P@ssw0rd!" while adding little
 * entropy. A 12-character minimum plus a block list of obvious choices is a
 * stronger real-world defence.
 */
const WEAK_PASSWORDS = [
    'password',
    'passw0rd',
    '123456789012',
    'qwertyuiop',
    'letmein',
    'blackbox',
    'blackboxtalent',
    'administrator',
];
export const passwordSchema = z
    .string()
    .min(LIMITS.password.min, `Use at least ${LIMITS.password.min} characters.`)
    .max(LIMITS.password.max)
    .refine((value) => !WEAK_PASSWORDS.some((weak) => value.toLowerCase().includes(weak)), 'That password contains a commonly guessed word. Choose something less predictable.');
export const nameSchema = z.string().trim().min(LIMITS.name.min).max(LIMITS.name.max);
/** SEO fields shared by posts and pages. */
export const seoSchema = z.object({
    metaTitle: z.string().trim().max(LIMITS.metaTitle.max).nullish(),
    metaDescription: z.string().trim().max(LIMITS.metaDescription.max).nullish(),
    ogImageId: uuidSchema.nullish(),
    /** Set when this URL is a duplicate of another; emitted as <link rel=canonical>. */
    canonicalUrl: z.url().nullish(),
    /** Emits <meta name="robots" content="noindex">. */
    noIndex: z.boolean().default(false),
});
export const sortDirectionSchema = z.enum(['ASC', 'DESC']).default('DESC');
/**
 * Query-string pagination. `coerce` is required because everything arriving in a
 * URL is a string.
 */
export const paginationQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
});
export function buildPaginationMeta(total, page, limit) {
    const totalPages = limit > 0 ? Math.ceil(total / limit) : 0;
    return {
        page,
        limit,
        total,
        totalPages,
        hasNextPage: page < totalPages,
        hasPreviousPage: page > 1 && totalPages > 0,
    };
}
export function patchSchemaOf(shape) {
    /*
      Two casts, and they are the reason this is a helper with a test rather than an
      expression inlined at four call sites.
  
      `ZodDefault.unwrap()` is typed as Zod's *internal* `$ZodType`, which does not carry the
      public `.optional()`; and `z.object()` over a dynamically built shape cannot be related
      by the compiler to the mapped type in the signature, even though the two are
      constructed in lockstep. Neither is a claim the type system can check, so the guarantee
      comes from `patch-schema.spec.ts` in the API — which asserts the one property that matters: that an
      absent key stays absent in the output.
    */
    const patched = Object.fromEntries(Object.entries(shape).map(([key, field]) => {
        const base = (field instanceof z.ZodDefault ? field.unwrap() : field);
        return [key, base.optional()];
    }));
    return z.object(patched);
}
/**
 * Payload for every drag-and-drop reorder endpoint (blog posts, menu items,
 * pages). Sending the whole ordered list rather than one moved item makes the
 * operation idempotent and immune to lost updates when two admins reorder
 * concurrently — last write wins on a complete, coherent list instead of
 * producing an interleaved mess.
 */
export const reorderSchema = z.object({
    items: z
        .array(z.object({ id: uuidSchema, sortOrder: z.number().int().min(0) }))
        .min(1)
        .max(500),
});
//# sourceMappingURL=common.js.map